import { useEffect, useState } from 'react';
import type { MouseEvent } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  Alert,
  CircularProgress,
  Grid,
  Button,
  Avatar,
  useTheme,
  useMediaQuery,
  Breadcrumbs,
  Link,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  AttachMoney as AttachMoneyIcon,
  NavigateNext as NavigateNextIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  EventBusy as EventBusyIcon,
} from '@mui/icons-material';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase';
import type { Service, Tenant } from '../../lib/db/types';
import { getPublicBookingCatalog } from '../../lib/bookingApi';

interface ServiceWithId extends Service {
  id: string;
}

interface TenantWithId extends Tenant {
  id: string;
}

interface TimeSlot {
  startTime: string;
  endTime: string;
  available: boolean;
  remainingSpots?: number;
}

interface WeekDay {
  date: Date;
  dateString: string;
  dayName: string;
  dayNumber: number;
  isToday: boolean;
}

export function BookingTimePage() {
  const { tenantSlug, serviceId, providerId } = useParams<{ tenantSlug: string; serviceId: string; providerId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [tenant, setTenant] = useState<TenantWithId | null>(null);
  const [service, setService] = useState<ServiceWithId | null>(null);
  const [provider, setProvider] = useState<{ name: string; photoUrl?: string } | null>(null);
  
  const [weekDays, setWeekDays] = useState<WeekDay[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(new Date());
  
  const [slots, setSlots] = useState<TimeSlot[]>([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const generateWeekDays = (startDate: Date): WeekDay[] => {
    const days: WeekDay[] = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    for (let i = 0; i < 7; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);
      date.setHours(0, 0, 0, 0);
      
      const dateString = date.toISOString().split('T')[0];
      
      days.push({
        date,
        dateString,
        dayName: dayNames[date.getDay()],
        dayNumber: date.getDate(),
        isToday: date.getTime() === today.getTime(),
      });
    }
    
    return days;
  };

  const getWeekStart = (date: Date): Date => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  };

  useEffect(() => {
    if (!tenantSlug || !serviceId || !providerId) {
      setError('Invalid booking link');
      setLoading(false);
      return;
    }

    const loadData = async () => {
      try {
        const catalog = await getPublicBookingCatalog(tenantSlug);
        const tenantData = catalog.tenant as TenantWithId;
        setTenant(tenantData);

        const serviceData = catalog.services.find((candidate) => candidate.id === serviceId) as ServiceWithId | undefined;
        if (!serviceData) {
          setError('Service not found');
          setLoading(false);
          return;
        }

        setService(serviceData);

        const providerData = catalog.providers.find((candidate) => candidate.id === providerId);
        if (providerData && serviceData.providerIds?.includes(providerId)) {
          setProvider({
            name: providerData.name,
            photoUrl: providerData.photoUrl,
          });
        } else {
          setProvider({ name: 'Unknown Provider' });
        }

        const weekStart = getWeekStart(new Date());
        setCurrentWeekStart(weekStart);
        const days = generateWeekDays(weekStart);
        setWeekDays(days);
        
        const today = days.find(d => d.isToday);
        setSelectedDate(today?.dateString || days[0]?.dateString);
      } catch (err: any) {
        console.error('Error loading data:', err);
        setError('Failed to load booking information');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [tenantSlug, serviceId, providerId]);

  useEffect(() => {
    if (!tenant || !service || !providerId || !selectedDate) return;

    const fetchAvailability = async () => {
      setSlotsLoading(true);
      try {
        const calculateAvailability = httpsCallable(functions, 'calculateAvailability');
        const result = await calculateAvailability({
          tenantSlug,
          serviceId: service.id,
          providerId: providerId,
          date: selectedDate,
        });
        
        const data = result.data as { slots: TimeSlot[] };
        setSlots(data.slots || []);
      } catch (err: any) {
        console.error('Error fetching availability:', err);
        setSlots([]);
      } finally {
        setSlotsLoading(false);
      }
    };

    fetchAvailability();
  }, [tenant, service, providerId, selectedDate, tenantSlug]);

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) return `${hours} hr`;
    return `${hours} hr ${remainingMinutes} min`;
  };

  const formatPrice = (price?: number, currency?: string): string => {
    if (price === undefined || price === null) return 'Free';
    const dollars = price / 100;
    const currencySymbol = currency === 'EUR' ? '€' : '$';
    return `${currencySymbol}${dollars.toFixed(2)}`;
  };

  const formatTime = (timeStr: string): string => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  const handleDateChange = (_: MouseEvent<HTMLElement>, dateString: string | null) => {
    if (dateString) {
      setSelectedDate(dateString);
    }
  };

  const handlePreviousWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() - 7);
    setCurrentWeekStart(newWeekStart);
    setWeekDays(generateWeekDays(newWeekStart));
  };

  const handleNextWeek = () => {
    const newWeekStart = new Date(currentWeekStart);
    newWeekStart.setDate(newWeekStart.getDate() + 7);
    setCurrentWeekStart(newWeekStart);
    setWeekDays(generateWeekDays(newWeekStart));
  };

  const handleSelectSlot = (slot: TimeSlot) => {
    if (!slot.available) return;
    
    navigate(`/book/${tenantSlug}/service/${serviceId}/provider/${providerId}/contact`, {
      state: {
        tenant,
        service,
        provider,
        providerId,
        selectedDate,
        selectedTime: slot.startTime,
        selectedEndTime: slot.endTime,
        reschedulePublicAccessHash: (location.state as any)?.reschedulePublicAccessHash,
      },
    });
  };

  const handleBackToProviders = () => {
    navigate(`/book/${tenantSlug}/service/${serviceId}`);
  };

  const handleBackToServices = () => {
    navigate(`/book/${tenantSlug}`);
  };

  const primaryColor = tenant?.branding?.primaryColor || theme.palette.primary.main;
  const isGroupClass = service && service.attendantsNumber > 1;

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  if (!tenant || !service) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: 'grey.50',
          p: 3,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          Booking not found
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'grey.50',
        pb: 4,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          bgcolor: 'white',
          borderBottom: '1px solid',
          borderColor: 'divider',
          py: { xs: 2, sm: 3 },
          px: { xs: 2, sm: 3 },
          mb: { xs: 2, sm: 4 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexDirection: { xs: 'column', sm: 'row' },
              textAlign: { xs: 'center', sm: 'left' },
            }}
          >
            {tenant.branding?.logoUrl ? (
              <Box
                component="img"
                src={tenant.branding.logoUrl}
                alt={tenant.name}
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  objectFit: 'contain',
                  borderRadius: 1,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: { xs: 48, sm: 56 },
                  height: { xs: 48, sm: 56 },
                  borderRadius: 1,
                  bgcolor: primaryColor,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: { xs: '1.5rem', sm: '2rem' },
                  fontWeight: 'bold',
                }}
              >
                {tenant.name.charAt(0).toUpperCase()}
              </Box>
            )}
            <Box>
              <Typography
                variant="h5"
                component="h1"
                fontWeight={600}
                sx={{ color: 'text.primary' }}
              >
                {tenant.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Book an appointment
              </Typography>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        {/* Breadcrumbs */}
        <Breadcrumbs
          separator={<NavigateNextIcon fontSize="small" />}
          sx={{ mb: 2 }}
        >
          <Link
            component="button"
            variant="body2"
            onClick={handleBackToServices}
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': { color: primaryColor },
            }}
          >
            Services
          </Link>
          <Link
            component="button"
            variant="body2"
            onClick={handleBackToProviders}
            sx={{
              color: 'text.secondary',
              textDecoration: 'none',
              '&:hover': { color: primaryColor },
            }}
          >
            Providers
          </Link>
          <Typography variant="body2" color="text.primary">
            Select Time
          </Typography>
        </Breadcrumbs>

        {/* Service + Provider Summary */}
        <Card
          sx={{
            mb: 4,
            borderLeft: 4,
            borderColor: service.color || primaryColor,
          }}
        >
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 3 },
                alignItems: { xs: 'flex-start', sm: 'center' },
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant={isMobile ? 'h6' : 'h5'}
                  component="h2"
                  fontWeight={600}
                  sx={{ mb: 0.5 }}
                >
                  {service.name}
                </Typography>
                
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: { xs: 1, sm: 2 },
                    mb: { xs: 1, sm: 0 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'text.secondary',
                    }}
                  >
                    <AccessTimeIcon fontSize="small" />
                    <Typography variant="body2">
                      {formatDuration(service.duration)}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      color: 'text.secondary',
                    }}
                  >
                    <AttachMoneyIcon fontSize="small" />
                    <Typography variant="body2" fontWeight={500}>
                      {formatPrice(service.price, service.currency)}
                    </Typography>
                  </Box>
                  
                  {isGroupClass && (
                    <Chip
                      label={`${service.attendantsNumber} spots per class`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  )}
                </Box>
              </Box>

              {provider && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    bgcolor: 'grey.50',
                    p: 1.5,
                    borderRadius: 2,
                  }}
                >
                  <Avatar
                    src={provider.photoUrl}
                    alt={provider.name}
                    sx={{
                      width: 40,
                      height: 40,
                      bgcolor: primaryColor,
                    }}
                  >
                    {provider.name.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Provider
                    </Typography>
                    <Typography variant="body1" fontWeight={500}>
                      {provider.name}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Page Title */}
        <Typography
          variant={isMobile ? 'h6' : 'h5'}
          component="h2"
          fontWeight={600}
          sx={{ mb: 3 }}
        >
          Select a Date & Time
        </Typography>

        {/* Calendar Navigation */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handlePreviousWeek}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Previous
          </Button>
          
          <Typography variant="body1" fontWeight={500}>
            {currentWeekStart.toLocaleDateString('en-US', {
              month: 'long',
              year: 'numeric',
            })}
          </Typography>
          
          <Button
            endIcon={<ArrowForwardIcon />}
            onClick={handleNextWeek}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            Next
          </Button>
        </Box>

        {/* Week Day Selector */}
        <ToggleButtonGroup
          value={selectedDate}
          exclusive
          onChange={handleDateChange}
          aria-label="Select date"
          sx={{
            width: '100%',
            mb: 4,
            display: 'flex',
            flexWrap: { xs: 'wrap', sm: 'nowrap' },
            gap: { xs: 0.5, sm: 0 },
            '& .MuiToggleButtonGroup-grouped': {
              flex: { xs: '1 1 calc(25% - 4px)', sm: 1 },
              minWidth: { xs: 'auto', sm: 0 },
              borderRadius: { xs: 1, sm: 0 },
              border: '1px solid',
              borderColor: 'divider',
              '&.Mui-selected': {
                bgcolor: primaryColor,
                color: 'white',
                '&:hover': {
                  bgcolor: primaryColor,
                  filter: 'brightness(0.9)',
                },
              },
              '&:first-of-type': {
                borderRadius: { xs: 1, sm: '4px 0 0 4px' },
              },
              '&:last-of-type': {
                borderRadius: { xs: 1, sm: '0 4px 4px 0' },
              },
            },
          }}
        >
          {weekDays.map((day) => (
            <ToggleButton
              key={day.dateString}
              value={day.dateString}
              aria-label={day.dateString}
              sx={{
                flexDirection: 'column',
                py: { xs: 1, sm: 1.5 },
                px: { xs: 0.5, sm: 1 },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 500,
                  textTransform: 'uppercase',
                  fontSize: '0.7rem',
                }}
              >
                {day.dayName}
              </Typography>
              <Typography
                variant="body1"
                fontWeight={600}
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                }}
              >
                {day.dayNumber}
              </Typography>
              {day.isToday && (
                <Chip
                  label="Today"
                  size="small"
                  sx={{
                    height: 16,
                    fontSize: '0.6rem',
                    mt: 0.5,
                    bgcolor: 'transparent',
                    border: 'none',
                    color: 'inherit',
                  }}
                />
              )}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Time Slots */}
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 2,
            }}
          >
            <Typography variant="h6" component="h3">
              Available Times
            </Typography>
            <Typography variant="body2" color="text.secondary">
              for {selectedDate && new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', {
                weekday: 'long',
                month: 'long',
                day: 'numeric',
              })}
            </Typography>
          </Box>

          {slotsLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                py: 6,
              }}
            >
              <CircularProgress size={32} />
            </Box>
          ) : slots.length === 0 ? (
            <Card
              sx={{
                textAlign: 'center',
                py: 6,
                bgcolor: 'grey.50',
              }}
            >
              <CardContent>
                <EventBusyIcon
                  sx={{
                    fontSize: 48,
                    color: 'text.disabled',
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  No availability
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  There are no available time slots for this date.
                  <Box component="span" sx={{ display: 'block' }}>
                    Please try selecting a different date.
                  </Box>
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Grid container spacing={2}>
              {slots.map((slot, index) => {
                const isAvailable = slot.available;
                const hasLimitedSpots = isGroupClass && 
                  slot.remainingSpots !== undefined && 
                  slot.remainingSpots > 0 && 
                  slot.remainingSpots <= 3;

                return (
                  <Grid item xs={6} sm={4} md={3} key={index}>
                    <Button
                      variant={isAvailable ? 'outlined' : 'text'}
                      fullWidth
                      disabled={!isAvailable}
                      onClick={() => handleSelectSlot(slot)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        textTransform: 'none',
                        borderWidth: 2,
                        borderColor: isAvailable ? primaryColor : 'transparent',
                        color: isAvailable ? primaryColor : 'text.disabled',
                        bgcolor: isAvailable ? 'transparent' : 'grey.100',
                        '&:hover': isAvailable
                          ? {
                              borderWidth: 2,
                              bgcolor: `${primaryColor}10`,
                            }
                          : undefined,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                        }}
                      >
                        <Typography
                          variant="body1"
                          fontWeight={600}
                          sx={{
                            color: isAvailable ? 'inherit' : 'text.disabled',
                          }}
                        >
                          {formatTime(slot.startTime)}
                        </Typography>
                        
                        {isGroupClass && slot.remainingSpots !== undefined && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: hasLimitedSpots
                                ? 'warning.main'
                                : 'text.secondary',
                              fontWeight: hasLimitedSpots ? 500 : 400,
                            }}
                          >
                            {slot.remainingSpots === 0
                              ? 'Full'
                              : slot.remainingSpots === 1
                              ? '1 spot left'
                              : `${slot.remainingSpots} spots left`}
                          </Typography>
                        )}
                      </Box>
                    </Button>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Container>

      {/* Footer */}
      <Box
        sx={{
          mt: 6,
          py: 3,
          textAlign: 'center',
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" color="text.secondary">
            Powered by{' '}
            <Box component="span" sx={{ fontWeight: 700 }}>
              FIT
            </Box>
            <Box component="span" sx={{ fontStyle: 'italic' }}>
              Stac
            </Box>
          </Typography>
        </Container>
      </Box>
    </Box>
  );
}

export default BookingTimePage;
